<script setup>
  import {computed, ref, watch} from "vue";

  import ComponentStatusBadge from "@components/Badges/StatusBadge.vue";
  import ComponentDropdown from "@components/Dropdown/Dropdown.vue";
  import ComponentIcon from "@components/Icons/Icon.vue";
  import {useForm} from "@inertiajs/vue3";

  defineOptions({
    name: "ComponentRecordStatus",
  });

  const props = defineProps({
    record: {
      type: Object,
      default: () => ({}),
    },
  });

  const emit = defineEmits(["update:status"]);

  const refDropdown = ref(null);

  const form = useForm({
    status: props.record?.status,
  });

  if (props.record) {
    watch(
      () => props.record.status,
      (newStatus) => {
        form.status = newStatus;
      },
    );
  }

  const statusName = {
    inProgress: "in Bearbeitung",
    discarded: "irrelevant",
    completed: "abgeschlossen",
    default: "offen",
  };

  function save(status) {
    form.status = status;
    refDropdown.value.close();

    emit("update:status", status);

    if (!props.record) {
      return;
    }

    form.patch(route("records.update", {patient: props.record.patient_id, record: props.record.id}), {
      preserveScroll: true,
      only: ["flash", "errors", "records", "record"],
      headers: {
        // avoid updating the edited_at values when just changing the status
        "Mc-Silent-Update": true,
      },
      optimistic: (pageProperties) => {
        const result = {};

        if (pageProperties.record?.id === props.record.id) {
          result.record = {...pageProperties.record, status};
        }

        if (pageProperties.records?.data) {
          result.records = {
            ...pageProperties.records,
            data: pageProperties.records.data.map((r) => (r.id === props.record.id ? {...r, status} : r)),
          };
        }

        return result;
      },
    });
  }

  const badgeColor = computed(() => {
    switch (form.status) {
      case "inProgress":
        return "orange";
      case "discarded":
        return "gray";
      case "completed":
        return "green";
      default:
        return "blue";
    }
  });

  const dropdownColorClass = computed(() => {
    switch (form.status) {
      case "inProgress":
        return "bg-orange-100 text-orange-800 hover:bg-orange-300";
      case "discarded":
        return "bg-gray-100 text-gray-800 hover:bg-gray-300";
      case "completed":
        return "bg-mcgreen-100 text-mcgreen-800 hover:bg-mcgreen-300";
      default:
        return "bg-mcblue-100 text-mcblue-800 hover:bg-mcblue-300";
    }
  });
</script>

<template>
  <ComponentDropdown ref="refDropdown" class="rounded-full p-px" :class="dropdownColorClass" alignment="left">
    <template #button>
      <div class="flex items-center justify-end">
        <ComponentStatusBadge
          class="whitespace-nowrap"
          :label="statusName[form.status || 'default']"
          :color="badgeColor"
        />

        <ComponentIcon class="-ml-0.5">expand_more</ComponentIcon>
      </div>
    </template>

    <template #dropdown>
      <ul class="flex-row space-y-px bg-white text-xs text-gray-900">
        <li>
          <button class="w-full bg-mcblue-100 p-1 whitespace-nowrap hover:bg-mcblue-200" @click="save(null)">
            offen
          </button>
        </li>
        <li>
          <button class="w-full bg-orange-100 p-1 whitespace-nowrap hover:bg-orange-200" @click="save('inProgress')">
            in Bearbeitung
          </button>
        </li>
        <li>
          <button class="w-full bg-mcgreen-100 p-1 whitespace-nowrap hover:bg-mcgreen-200" @click="save('completed')">
            abgeschlossen
          </button>
        </li>
        <li>
          <button class="w-full bg-gray-100 p-1 whitespace-nowrap hover:bg-gray-200" @click="save('discarded')">
            irrelevant
          </button>
        </li>
      </ul>
    </template>
  </ComponentDropdown>
</template>
